import { Avatar, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { StatusBadge } from './StatusBadge';
import { OverrideWarning } from './OverrideWarning';
import { formatRelativeTime } from '../../../../utils/formatters';
import type { MatchingSessionListItem } from '../../../../services/api/matchingSessions';

interface MatchingSessionRowProps {
  session: MatchingSessionListItem;
  isResuming: boolean;
  onResume: (sessionId: string) => void;
  /** Opens the session in the in-app matching tool (setup or progress). */
  onContinue: (sessionId: string) => void;
}

const ERROR_TRUNCATION_LIMIT = 80;

function truncateError(message: string | null): string {
  if (!message) return '';
  return message.length > ERROR_TRUNCATION_LIMIT
    ? `${message.slice(0, ERROR_TRUNCATION_LIMIT)}…`
    : message;
}

function ownerDisplayName(session: MatchingSessionListItem): string {
  const first = session.owner_first_name?.trim() ?? '';
  const last = session.owner_last_name?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  return combined.length > 0 ? combined : 'Unknown user';
}

function ownerInitials(session: MatchingSessionListItem): string {
  const first = session.owner_first_name?.charAt(0) ?? '';
  const last = session.owner_last_name?.charAt(0) ?? '';
  const combined = `${first}${last}`.toUpperCase();
  return combined.length > 0 ? combined : '?';
}

/**
 * Single row in the matching sessions panel (MED-829 / E23-8).
 *
 * Every admin sees the same actions, whoever started the session: any staff
 * user may resume any session (medley-api checks the role, not the owner).
 * - submitting → "Starting…" inline
 * - expired → no actions (expired rows aren't listed today)
 * - any other status → Continue (the in-app matching tool, which decides
 *   between setup and progress after it connects) and Resume (the
 *   matching-server editor in a new tab)
 */
export function MatchingSessionRow({
  session,
  isResuming,
  onResume,
  onContinue,
}: MatchingSessionRowProps) {
  const showOverrideBadge = session.status === 'completed' && session.user_in_valid === true;
  const showFailedError = session.status === 'failed' && session.error_message !== null;

  const renderActions = () => {
    if (session.status === 'submitting') {
      return (
        <Text size="sm" c="dimmed" fs="italic">
          Starting…
        </Text>
      );
    }
    if (session.status === 'expired') return null;
    return (
      <>
        <Button
          size="sm"
          variant="light"
          onClick={() => onContinue(session.id)}
          aria-label={`Continue matching session from ${formatRelativeTime(session.updated_at)}`}
        >
          Continue
        </Button>
        <Button
          size="sm"
          variant="light"
          loading={isResuming}
          onClick={() => onResume(session.id)}
          aria-label={`Resume matching session from ${formatRelativeTime(session.updated_at)}`}
        >
          Resume
        </Button>
      </>
    );
  };

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      align="flex-start"
      py="sm"
      px="md"
      style={{
        borderRadius: 8,
        border: '1px solid var(--mantine-color-gray-2)',
        backgroundColor: 'var(--mantine-color-white)',
      }}
    >
      <Group gap="md" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
        <StatusBadge status={session.status} />
        <Group gap="xs" wrap="nowrap" align="center">
          <Tooltip label={ownerDisplayName(session)} withArrow>
            <Avatar
              src={session.owner_avatar_url ?? undefined}
              radius="xl"
              size="sm"
              alt={ownerDisplayName(session)}
            >
              {ownerInitials(session)}
            </Avatar>
          </Tooltip>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {ownerDisplayName(session)}
            </Text>
            <Text size="xs" c="dimmed">
              {formatRelativeTime(session.updated_at)}
            </Text>
          </Stack>
        </Group>
        {showOverrideBadge && <OverrideWarning compact />}
        {showFailedError && (
          <Text size="xs" c="dangerRed" style={{ flexShrink: 1, minWidth: 0 }} truncate>
            {truncateError(session.error_message)}
          </Text>
        )}
      </Group>

      <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
        {renderActions()}
      </Group>
    </Group>
  );
}
